import { useForm, Controller } from "react-hook-form";
import { Spin, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import AnimatedMainContainer from "../../../../components/AnimatedMainContainer";
import {
  ActionButton,
  Container,
  ContainerButtons,
  Horizontal,
  InputTitle,
  PreviewImage,
  UploadButton,
  CustomInput,
  PreviewBackgroundContainer,
  PreviewLogoContainer,
  RemoveImageButton,
  FieldWrapper,
  TrashIconContainer,
  SelectCustom,
  MultiSelectCustom,
} from "../../styles";
import { useIntl } from "react-intl";
import { useEffect, useState, useRef } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSelector } from "react-redux";
import api from "../../../../service";
import { MdOutlineCancel, MdWarning } from "react-icons/md";
import { FaRegSave } from "react-icons/fa";
import { getLogoSrcFromHtml } from "../../../../utils/htmlManipulation";
import { FaRegTrashAlt } from "react-icons/fa";
import {
  createTotem,
  getTotemById,
  updateTotem,
} from "../../../../service/manage.access";
import { useToast } from "../../../../hooks/toast";
import { useParams } from "react-router-dom";
import { getActiveSortedEvents } from "../../../../service/event.service";
import credentialTypes from "../../../../constants/credentialTypes";

// Opções de tipo de impressão de credencial
const PRINT_CONFIGURATION_OPTIONS = [
  { value: "STAMP", labelKey: "print_config.stamp" },
  { value: "TEMPLATE", labelKey: "print_config.template" },
];

// Constante para o delay do scroll e focus
const SCROLL_FOCUS_DELAY = 500;

export const ManageAccessForm = () => {
  const intl = useIntl();
  const [backgroundFileList, setBackgroundFileList] = useState([]);
  const [events, setEvents] = useState([]);
  const { token } = useSelector((state) => state.auth);
  const [logoUrl, setLogoUrl] = useState(null);
  const { addToast } = useToast();
  const { id } = useParams();
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isLoadingTotem, setIsLoadingTotem] = useState(false);
  const [hasActiveTotemSession, setHasActiveTotemSession] = useState(false);

  const fieldRefs = useRef({
    event: null,
    printConfiguration: null,
    allowedCredentials: null,
    areaName: null,
    slug: null,
    totemAccessCode: null,
    backgroundImage: null,
  });

  // Mapeamento das opções de print configuration para o select
  const printConfigOptions = PRINT_CONFIGURATION_OPTIONS.map((option) => ({
    value: option.value,
    label: intl.formatMessage({ id: option.labelKey }),
  }));

  // Ordena alfabeticamente baseado no label traduzido
  const allowedCredentialsOptions = credentialTypes().sort((a, b) =>
    a.label.localeCompare(b.label, intl.locale)
  );

  const schema = z.object({
    event: z
      .object({
        value: z.string(),
        label: z.string(),
        idNumber: z.number().optional(),
      })
      .nullable()
      .refine((val) => !!val, {
        message: intl.formatMessage({ id: "required_field" }),
      }),

    printConfiguration: z
      .string()
      .nullable()
      .refine((val) => !!val, {
        message: intl.formatMessage({ id: "required_field" }),
      }),

    allowedCredentials: z.array(z.string()).optional(),

    areaName: z.string().min(1, intl.formatMessage({ id: "required_field" })),
    slug: z.string().min(1, intl.formatMessage({ id: "required_field" })),
    totemAccessCode: z
      .string()
      .min(1, intl.formatMessage({ id: "required_field" }))
      .regex(/^[a-zA-Z0-9]+$/, intl.formatMessage({ id: "invalid_value" })),
    backgroundImage: z.any().refine(
      (val) => {
        if (!id) {
          return Array.isArray(val) && val.length > 0;
        }
        return true;
      },
      {
        message: intl.formatMessage({ id: "required_field" }),
      }
    ),
  });

  const {
    control,
    handleSubmit,
    setError,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    criteriaMode: "all",
    resolver: zodResolver(schema),
    defaultValues: {
      event: null,
      printConfiguration: null,
      allowedCredentials: [],
      areaName: "",
      slug: "",
      totemAccessCode: "",
      backgroundImage: [],
    },
  });

  const selectedEventEditionCode = watch("event")?.value;
  const printConfiguration = watch("printConfiguration");
  const isLoading = isLoadingEvents || isLoadingTotem;

  useEffect(() => {
    const fetchAllEvents = async () => {
      try {
        setIsLoadingEvents(true);

        const mapped = await getActiveSortedEvents({ token });

        setEvents(mapped);
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: "api.error." + error.response?.data?.code,
          }),
        });
      } finally {
        setIsLoadingEvents(false);
      }
    };

    fetchAllEvents();
  }, [token, addToast, intl]);

  useEffect(() => {
    if (!id || events.length === 0) return;

    const fetchTotemById = async () => {
      try {
        setIsLoadingTotem(true);
        const response = await getTotemById({ token, id });
        const totemData = response.data.totem;

        const foundEvent = events.find(
          (ev) => ev.idNumber === totemData.eventId
        );

        reset({
          event: foundEvent || null,
          printConfiguration: totemData.printConfiguration || null,
          allowedCredentials: totemData.allowedCredentials || [],
          areaName: totemData.areaName,
          slug: totemData.slug,
          totemAccessCode: totemData.totemAccessCode,
          backgroundImage: [],
        });

        setHasActiveTotemSession(totemData.hasActiveTotemSession || false);

        if (totemData.backgroundImage) {
          setBackgroundFileList([totemData.backgroundImage]);
        }
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: "api.error." + error.response.data.code,
          }),
        });
      } finally {
        setIsLoadingTotem(false);
      }
    };

    fetchTotemById();
  }, [id, events, token, reset, addToast, intl]);

  useEffect(() => {
    if (!selectedEventEditionCode) return;

    const fetchEventById = async () => {
      try {
        const response = await api.get(
          `/admin/events/${selectedEventEditionCode}`,
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setLogoUrl(getLogoSrcFromHtml(response.data.output.logo));
      } catch (error) {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + error.response.data.code,
          }),
        });
      }
    };

    fetchEventById();
  }, [selectedEventEditionCode, token, addToast, intl]);

  useEffect(() => {
    if (printConfiguration && printConfiguration !== "STAMP") {
      reset((formValues) => ({
        ...formValues,
        allowedCredentials: [],
      }));
    }
  }, [printConfiguration, reset]);

  useEffect(() => {
    const firstErrorField = Object.keys(errors)[0];

    if (firstErrorField && fieldRefs.current[firstErrorField]) {
      const element = fieldRefs.current[firstErrorField];

      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      setTimeout(() => {
        const input = element.querySelector("input");

        const select = element.querySelector(".ant-select");
        const selectSelector = element.querySelector(".ant-select-selector");

        if (input) {
          input.focus();
          return;
        }

        if (selectSelector) {
          selectSelector.click();
        }

        setTimeout(() => {
          const selectInput = select?.querySelector("input");
          selectInput?.focus();
        }, 0);
      }, SCROLL_FOCUS_DELAY);
    }
  }, [errors]);

  const onSubmit = async (data) => {
    if (data.printConfiguration === "STAMP") {
      if (!data.allowedCredentials || data.allowedCredentials.length === 0) {
        setError("allowedCredentials", {
          type: "required",
          message: intl.formatMessage({ id: "required_field" }),
        });
        return;
      }
    }

    const formData = new FormData();
    formData.append("eventId", data.event?.idNumber);
    formData.append("printConfiguration", data.printConfiguration);

    if (
      data.printConfiguration === "STAMP" &&
      data.allowedCredentials?.length > 0
    ) {
      data.allowedCredentials.forEach((credential) => {
        formData.append("allowedCredentials", credential);
      });
    }

    formData.append("areaName", data.areaName);
    formData.append("slug", data.slug);
    formData.append("totemAccessCode", data.totemAccessCode);

    if (backgroundFileList[0] instanceof File) {
      formData.append("backgroundImage", backgroundFileList[0]);
    }

    try {
      if (id) {
        await updateTotem({ token, id, data: formData });

        addToast({
          type: "success",
          title: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: "success.updated_totem",
          }),
        });
      } else {
        await createTotem({ token, data: formData });

        addToast({
          type: "success",
          title: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: "success.register_totem",
          }),
        });
      }

      reset();
      window.history.back();
    } catch (error) {
      console.error(error);

      addToast({
        type: "error",
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "api.error." + error.response?.data?.code,
        }),
      });
    }
  };

  return (
    <AnimatedMainContainer
      breadcrumb={
        id
          ? intl.formatMessage({ id: "edit_totem" })
          : intl.formatMessage({ id: "register_new_totem" })
      }
    >
      <Spin spinning={isLoading}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Container>
            {hasActiveTotemSession && (
              <div
                style={{
                  backgroundColor: "#FEE2E2",
                  border: "1px solid #DC2626",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "24px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  width: "100%",
                }}
              >
                <MdWarning size={24} color="#DC2626" />
                <p style={{ margin: 0, color: "#991B1B", fontWeight: 600 }}>
                  {intl.formatMessage({ id: "active_totem_sessions_warning" })}
                </p>
              </div>
            )}
            <Horizontal>
              <FieldWrapper ref={(el) => (fieldRefs.current.event = el)}>
                <InputTitle>
                  {intl.formatMessage({ id: "event" })} <p>*</p>
                </InputTitle>
                <Controller
                  name="event"
                  control={control}
                  render={({ field }) => {
                    const { onChange, value, ...restField } = field;
                    return (
                      <SelectCustom
                        {...restField}
                        value={value ?? undefined}
                        placeholder={intl.formatMessage({ id: "select_event" })}
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        options={events}
                        onChange={(selectValue, option) => {
                          onChange(option);
                        }}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                      />
                    );
                  }}
                />
                {errors.event && (
                  <p style={{ color: "red" }}>{errors.event.message}</p>
                )}
              </FieldWrapper>

              <FieldWrapper ref={(el) => (fieldRefs.current.areaName = el)}>
                <InputTitle>
                  {intl.formatMessage({ id: "area_name" })}
                  <p>*</p>
                </InputTitle>
                <Controller
                  name="areaName"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      max={100}
                      placeholder={intl.formatMessage({
                        id: "area_name_placeholder",
                      })}
                    />
                  )}
                />
                {errors.areaName && (
                  <p style={{ color: "red" }}>{errors.areaName.message}</p>
                )}
              </FieldWrapper>
            </Horizontal>

            <Horizontal>
              <FieldWrapper ref={(el) => (fieldRefs.current.slug = el)}>
                <InputTitle>
                  URL (slug) <p>*</p>
                </InputTitle>
                <Controller
                  name="slug"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      max={100}
                      placeholder={intl.formatMessage({
                        id: "slug_placeholder",
                      })}
                      onChange={(e) => {
                        const value = e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, "");

                        field.onChange(value);
                      }}
                    />
                  )}
                />
                {errors.slug && (
                  <p style={{ color: "red" }}>{errors.slug.message}</p>
                )}
              </FieldWrapper>

              <FieldWrapper
                ref={(el) => (fieldRefs.current.totemAccessCode = el)}
              >
                <InputTitle>
                  {intl.formatMessage({ id: "totem_access_code" })}
                  <p>*</p>
                </InputTitle>
                <Controller
                  name="totemAccessCode"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      max={100}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^a-zA-Z0-9]/g,
                          ""
                        );
                        field.onChange(value);
                      }}
                      placeholder={intl.formatMessage({
                        id: "totem_access_code_placeholder",
                      })}
                    />
                  )}
                />
                {errors.totemAccessCode && (
                  <p style={{ color: "red" }}>
                    {errors.totemAccessCode.message}
                  </p>
                )}
              </FieldWrapper>

              <FieldWrapper
                ref={(el) => (fieldRefs.current.printConfiguration = el)}
              >
                <InputTitle>
                  {intl.formatMessage({ id: "print_configuration_label" })}{" "}
                  <p>*</p>
                </InputTitle>
                <Controller
                  name="printConfiguration"
                  control={control}
                  render={({ field }) => {
                    const { onChange, value, ...restField } = field;
                    return (
                      <SelectCustom
                        {...restField}
                        value={value ?? undefined}
                        placeholder={intl.formatMessage({
                          id: "print_configuration_placeholder",
                        })}
                        allowClear
                        options={printConfigOptions}
                        onChange={(selectValue) => {
                          onChange(selectValue);
                        }}
                      />
                    );
                  }}
                />
                {errors.printConfiguration && (
                  <p style={{ color: "red" }}>
                    {errors.printConfiguration.message}
                  </p>
                )}
              </FieldWrapper>
            </Horizontal>

            {printConfiguration === "STAMP" && (
              <Horizontal style={{ marginBottom: "32px" }}>
                <FieldWrapper
                  ref={(el) => (fieldRefs.current.allowedCredentials = el)}
                >
                  <InputTitle>
                    {intl.formatMessage({ id: "allowed_credentials_label" })}{" "}
                    <p>*</p>
                  </InputTitle>
                  <Controller
                    name="allowedCredentials"
                    control={control}
                    render={({ field }) => {
                      const { onChange, value, ...restField } = field;
                      return (
                        <MultiSelectCustom
                          {...restField}
                          mode="multiple"
                          value={value ?? []}
                          placeholder={intl.formatMessage({
                            id: "allowed_credentials_placeholder",
                          })}
                          allowClear
                          maxTagCount={100}
                          maxTagPlaceholder={() => null}
                          showArrow={false}
                          getPopupContainer={(trigger) => trigger.parentElement}
                          options={allowedCredentialsOptions}
                          onChange={(selectValue) => {
                            onChange(selectValue);
                          }}
                        />
                      );
                    }}
                  />
                  {errors.allowedCredentials && (
                    <p style={{ color: "red", marginTop: "12px" }}>
                      {errors.allowedCredentials.message}
                    </p>
                  )}
                </FieldWrapper>
              </Horizontal>
            )}

            <Horizontal>
              <FieldWrapper
                ref={(el) => (fieldRefs.current.backgroundImage = el)}
              >
                <InputTitle>
                  {intl.formatMessage({ id: "background_image" })}
                  <p>*</p>
                </InputTitle>

                <Controller
                  name="backgroundImage"
                  control={control}
                  render={({ field }) => {
                    if (!field.value) field.onChange([]);

                    return (
                      <PreviewBackgroundContainer
                        hasImage={backgroundFileList.length > 0}
                      >
                        {backgroundFileList.length === 0 && (
                          <Upload
                            accept="image/*"
                            fileList={backgroundFileList}
                            beforeUpload={() => false}
                            showUploadList={false}
                            onChange={({ fileList }) => {
                              const files = fileList.map(
                                (file) => file.originFileObj || file
                              );
                              setBackgroundFileList(files);

                              field.onChange(files);
                            }}
                          >
                            <UploadButton type="button">
                              <UploadOutlined style={{ fontSize: 44 }} />
                              {intl.formatMessage({
                                id: "add_background_image",
                              })}
                            </UploadButton>
                          </Upload>
                        )}

                        {backgroundFileList.length > 0 && (
                          <TrashIconContainer>
                            <RemoveImageButton
                              type="button"
                              onClick={() => {
                                setBackgroundFileList([]);
                                setError("backgroundImage", {
                                  type: "required",
                                  message: intl.formatMessage({
                                    id: "required_field",
                                  }),
                                });
                                field.onChange([]);
                              }}
                            >
                              <FaRegTrashAlt style={{ fontSize: "20px" }} />
                            </RemoveImageButton>

                            <PreviewImage
                              src={
                                typeof backgroundFileList[0] === "string"
                                  ? backgroundFileList[0]
                                  : URL.createObjectURL(backgroundFileList[0])
                              }
                              alt="preview"
                            />
                          </TrashIconContainer>
                        )}
                      </PreviewBackgroundContainer>
                    );
                  }}
                />

                <span>
                  {`${intl.formatMessage({ id: "resolution" })}: 1920x1080`}
                </span>

                {errors.backgroundImage && (
                  <p style={{ color: "red" }}>
                    {errors.backgroundImage.message}
                  </p>
                )}
              </FieldWrapper>

              {logoUrl && (
                <FieldWrapper>
                  <InputTitle>
                    {intl.formatMessage({ id: "event_logo" })}
                  </InputTitle>
                  <PreviewLogoContainer>
                    <PreviewImage
                      style={{ padding: "15px" }}
                      src={logoUrl}
                      alt="Logo do evento"
                    />
                  </PreviewLogoContainer>
                </FieldWrapper>
              )}
            </Horizontal>

            <ContainerButtons>
              <ActionButton
                type="button"
                cancel
                onClick={() => window.history.back()}
              >
                <MdOutlineCancel size={20} />
                {intl.formatMessage({ id: "cancel" })}
              </ActionButton>

              <ActionButton onClick={handleSubmit(onSubmit)} save>
                <FaRegSave />
                {intl.formatMessage({ id: "save" })}
              </ActionButton>
            </ContainerButtons>
          </Container>
        </form>
      </Spin>
    </AnimatedMainContainer>
  );
};
